// «Cerrar» y «Cerrar los N» de «Han terminado» (flujo.md #6, Entrega 3): un clic, sin diálogo, con un aviso «Deshacer»
// de 6 s (como el de mover mesas). Cerrar le manda al agente su orden de cierre, que no se puede retirar: por eso la
// orden NO sale hasta que pasan los 6 s sin «Deshacer» (como «deshacer enviar» del correo). Mientras tanto la fila se
// queda en su sitio con el botón girando; sale de su bloque cuando el servidor lo confirma (llega por el socket). Si
// falla, el aviso dice por qué y la fila vuelve a tener su «Cerrar». El diálogo largo queda para «Despedir sin recoger».
//
// Estado propio y efímero (fuente única: este almacén): qué cierres esperan sus 6 s, cuáles se están mandando y cuáles
// ya salieron. Lo del servidor (ciclo, situación) no se toca: llega como siempre.
import { create } from 'zustand';
import { t } from '../../../i18n';
import { useToastStore } from '../../../stores/toast.store';
import { oficinaApi } from '../api';
import type { ContratadoDTO } from '../compartido/vocabulario';
import { mensajeError } from '../errores';
import { quien } from '../marco/estado';

export const DESHACER_CIERRE_MS = 6_000;

export type FaseCierre = 'espera' | 'enviando' | 'enviado';

interface EstadoCierres {
  fases: Record<string, FaseCierre>;
}

export const useCierresStore = create<EstadoCierres>(() => ({ fases: {} }));

const poner = (ids: readonly string[], fase: FaseCierre | null) =>
  useCierresStore.setState((s) => {
    const fases = { ...s.fases };
    for (const id of ids) {
      if (fase) fases[id] = fase;
      else delete fases[id];
    }
    return { fases };
  });

/** La fase del cierre de un agente (null = ninguno en marcha). */
export const useFaseCierre = (agentId: string): FaseCierre | null => useCierresStore((s) => s.fases[agentId] ?? null);

/** Cierra a estos agentes tras 6 s salvo «Deshacer». Los que ya tienen un cierre en marcha no se repiten. */
export function cerrarConDeshacer(agentes: readonly Pick<ContratadoDTO, 'agentId' | 'tema'>[]): void {
  const fases = useCierresStore.getState().fases;
  const lote = agentes.filter((c) => !fases[c.agentId]);
  if (!lote.length) return;
  const ids = lote.map((c) => c.agentId);
  poner(ids, 'espera');
  const toasts = useToastStore.getState();
  let aviso = '';
  const temporizador = setTimeout(() => {
    void mandar(lote);
  }, DESHACER_CIERRE_MS);
  aviso = toasts.push({
    message: lote.length === 1 ? t('oficinaV2.lista.cierre.uno', { nombre: quien(lote[0]!) }) : t('oficinaV2.lista.cierre.varios', { n: lote.length }),
    durationMs: DESHACER_CIERRE_MS,
    action: {
      label: t('oficinaV2.lista.cierre.deshacer'),
      onClick: () => {
        clearTimeout(temporizador);
        poner(ids, null);
        useToastStore.getState().dismiss(aviso);
      },
    },
  });
}

async function mandar(lote: readonly Pick<ContratadoDTO, 'agentId' | 'tema'>[]): Promise<void> {
  poner(
    lote.map((c) => c.agentId),
    'enviando',
  );
  await Promise.all(
    lote.map(async (c) => {
      try {
        await oficinaApi.cerrar(c.agentId);
        poner([c.agentId], 'enviado');
      } catch (e) {
        poner([c.agentId], null);
        useToastStore.getState().push({ message: t('oficinaV2.lista.cierre.error', { nombre: quien(c), motivo: mensajeError(e) }) });
      }
    }),
  );
}
